import React from 'react';
import styled from 'styled-components';
import type { MessageThreadDetail } from '../../../../lib/api';
import { MessageList } from '../MessageList';

interface HistoryViewProps {
  threads: MessageThreadDetail[];
  patientId: number | null;
  /** スレッドを選んで返信する（B-8 #5） */
  onReplyToThread: (threadId: string) => void;
  /** 返信できるか。退所などで送信が閉じている利用者ではボタンを出さない */
  canReply: boolean;
  /** 通知から開いたときに位置を合わせる対象（B-10 #5）。両方そろって初めて特定できる */
  focusThreadId?: string | null;
  focusMessageId?: number | null;
  /** 位置合わせに成功したら呼ぶ。呼び出し側で対象を外す */
  onFocusComplete?: () => void;
  error: Error | null;
}

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: #F5F5F5;
`;

const CenterContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const EmptyImage = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  opacity: 0.6;
`;

const EmptyTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const EmptyDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  color: ${({ theme }) => theme.colors.text.muted};
  margin: 0;
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.md};
  margin: 0;
  line-height: 1.6;
`;

/**
 * メッセージ履歴の表示。
 * ローディングは親（MessagePage）の PageDataBoundary が担うため、ここでは扱わない。
 */
export const HistoryView: React.FC<HistoryViewProps> = ({
  threads,
  patientId,
  onReplyToThread,
  canReply,
  focusThreadId,
  focusMessageId,
  onFocusComplete,
  error,
}) => {
  if (error) {
    return (
      <CenterContainer>
        <EmptyText>
          メッセージの読み込みに失敗しました。
          <br />
          しばらくしてから再度お試しください。
        </EmptyText>
      </CenterContainer>
    );
  }

  if (threads.length === 0) {
    return (
      <CenterContainer>
        <EmptyImage src="/images/empty-mailbox.png" alt="やりとりはありません" />
        <EmptyTitle>やりとりはありません</EmptyTitle>
        <EmptyDescription>連絡するとやりとりが表示されます。</EmptyDescription>
      </CenterContainer>
    );
  }

  return (
    <Container>
      <MessageList
        threads={threads}
        patientId={patientId}
        onReplyToThread={onReplyToThread}
        canReply={canReply}
        focusThreadId={focusThreadId}
        focusMessageId={focusMessageId}
        onFocusComplete={onFocusComplete}
      />
    </Container>
  );
};

export default HistoryView;
